import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icon.registry';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
}

/**
 * Generic, fully styleable dropdown — a drop-in replacement for the native
 * `<select>` (works with `[(ngModel)]` / `formControlName`). The open panel, the
 * selected/hover highlight and the scrollbar all use the project's teal theme
 * instead of the OS-native blue. Mirrors the auth country-select, but takes a
 * generic `{ value, label }[]` and is sized for filter rows.
 */
@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, IconComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CustomSelectComponent), multi: true },
  ],
  templateUrl: './custom-select.component.html',
  styleUrl: './custom-select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSelectComponent implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLElement>);

  readonly options = input<SelectOption[]>([]);
  readonly placeholder = input<string>('Select…');
  readonly searchable = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  /** Optional icon shown at the left of the trigger (e.g. 'calendar'). */
  readonly leadingIcon = input<IconName | null>(null);
  /** Multi-select mode: the value is a `string[]`, options are toggled (checkbox-style),
   *  and the panel stays open on pick. Off by default, so existing single-selects are unchanged. */
  readonly multiple = input<boolean>(false);

  /**
   * Single source of truth for the current selection. Multi-select uses the full array;
   * single-select only ever reads element [0]. Keeping ONE signal (rather than separate
   * `value` / `values`) means toggling `[multiple]` after a value is written can never
   * leave the two representations out of sync.
   */
  protected readonly values = signal<string[]>([]);
  protected readonly open = signal(false);
  /** True when the panel is rendered ABOVE the trigger because there was not enough room below.
   * Decided once per open — see openPanel. */
  protected readonly dropUp = signal(false);
  protected readonly search = signal('');
  protected readonly activeIndex = signal(-1);
  protected readonly disabledState = signal(false);

  private readonly listRef = viewChild<ElementRef<HTMLUListElement>>('list');
  private readonly searchRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private onChange: (value: string | string[]) => void = () => {};
  private onTouched: () => void = () => {};

  /** True when anything is selected (drives the trigger's active text colour). */
  protected readonly hasValue = computed(() =>
    this.multiple() ? this.values().length > 0 : !!this.values()[0],
  );

  protected readonly selectedLabel = computed(() => {
    const sel = this.values();
    const opts = this.options();
    if (this.multiple()) {
      if (sel.length === 0) return '';
      if (sel.length === 1) return opts.find(o => o.value === sel[0])?.label ?? '';
      return `${sel.length} selected`;
    }
    // Single-select: only the first value matters.
    return sel[0] ? (opts.find(o => o.value === sel[0])?.label ?? '') : '';
  });

  /** Whether an option is currently selected (single or multi). */
  protected isSelected(opt: SelectOption): boolean {
    return this.multiple() ? this.values().includes(opt.value) : this.values()[0] === opt.value;
  }

  protected readonly filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    const opts = this.options();
    if (!q) return opts;
    return opts.filter(o => o.label.toLowerCase().includes(q));
  });

  // --- ControlValueAccessor ---
  writeValue(value: string | string[]): void {
    if (this.multiple()) {
      this.values.set(Array.isArray(value) ? value.map(String) : []);
    } else {
      // Single-select stored as a 0-or-1-element array (same source of truth as multi).
      this.values.set(value != null && value !== '' ? [String(value)] : []);
    }
  }
  registerOnChange(fn: (value: string | string[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  protected toggle(): void {
    if (this.disabledState()) return;
    this.open() ? this.close() : this.openPanel();
  }

  /** Matches the panel's `max-height: 16rem` plus its 0.35rem offset, in px. */
  private static readonly PANEL_MAX_PX = 16 * 16 + 6;

  private openPanel(): void {
    // Decide the direction BEFORE showing it. The panel used to open downward unconditionally, so
    // a select near the bottom of the page — the transactions page-size picker being the obvious
    // one — had its last options cut off by the viewport edge with no way to reach them.
    // Measured against the viewport, not a parent: the panel is positioned in the page's own
    // coordinate space, so what matters is whether it fits on screen.
    const rect = this.el.nativeElement.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    // Only flip when below is genuinely too small AND above is the better of the two — otherwise
    // a control in a short viewport would flip to a space that is no roomier.
    this.dropUp.set(below < CustomSelectComponent.PANEL_MAX_PX && above > below);

    this.open.set(true);
    this.search.set('');
    this.activeIndex.set(this.filtered().findIndex(o => this.isSelected(o)));
    if (this.searchable()) {
      setTimeout(() => this.searchRef()?.nativeElement.focus(), 0);
    }
  }

  /**
   * The single source of truth for marking the control touched. Every path that dismisses
   * the panel — picking an option, Escape, a click outside — funnels through here, so
   * onTouched() must NOT be called by those callers as well or consumers see it twice.
   */
  protected close(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.search.set('');
    this.onTouched();
  }

  protected onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(this.filtered().findIndex(o => !o.disabled));
  }

  protected selectOption(opt: SelectOption): void {
    if (opt.disabled) return;
    if (this.multiple()) {
      // Toggle in the selection and keep the panel open so more can be picked.
      const cur = this.values();
      const next = cur.includes(opt.value)
        ? cur.filter(v => v !== opt.value)
        : [...cur, opt.value];
      this.values.set(next);
      this.onChange(next);
      return;
    }
    // Single-select: store as a 1-element array, but emit the bare string to consumers.
    this.values.set([opt.value]);
    this.onChange(opt.value);
    // No onTouched() here — close() owns it, and this is only reachable with the panel
    // open, so close() is guaranteed to fire it exactly once.
    this.close();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.open()) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openPanel();
      }
      return;
    }
    const items = this.filtered();
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Enter': {
        event.preventDefault();
        const opt = items[this.activeIndex()];
        if (opt && !opt.disabled) this.selectOption(opt);
        break;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex.set(this.nextSelectable(this.activeIndex(), dir, items));
        this.scrollActiveIntoView();
        break;
      }
    }
  }

  private nextSelectable(from: number, dir: number, items: SelectOption[]): number {
    if (items.length === 0) return -1;
    let i = from;
    for (let step = 0; step < items.length; step++) {
      i += dir;
      if (i < 0) i = items.length - 1;
      if (i >= items.length) i = 0;
      if (!items[i].disabled) return i;
    }
    return from;
  }

  private scrollActiveIntoView(): void {
    const list = this.listRef()?.nativeElement;
    const idx = this.activeIndex();
    if (!list || idx < 0) return;
    (list.children[idx] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.el.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
